import { motion } from "framer-motion";
import { AlertCircle, Globe, Target, Lightbulb, RefreshCw } from "lucide-react";

interface Props {
  query: string;
  onAction: (action: string) => void;
  reason?: "no-match" | "weak-coverage" | "uncertain-routing" | "insufficient";
}

export default function NoConfidenceState({ query, onAction, reason = "insufficient" }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-2xl mx-auto text-center py-16"
    >
      <div className="w-14 h-14 rounded-2xl bg-muted/60 border border-border flex items-center justify-center mx-auto mb-6">
        <AlertCircle className="w-6 h-6 text-muted-foreground" />
      </div>

      <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-3">
        Not enough data for a strong decision
      </h2>
      <p className="text-muted-foreground font-body text-sm max-w-md mx-auto mb-3 leading-relaxed">
        This decision is based on estimated market data. We couldn't verify enough live retailer offers for this query in your region.
      </p>
      <p className="text-xs text-muted-foreground/60 font-body mb-8">
        Query: {query}
      </p>

      <div className="max-w-md mx-auto mb-8">
        <p className="text-xs text-muted-foreground font-body font-medium mb-3 uppercase tracking-wider">Try adjusting</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: Target, label: "Be more specific", desc: "Add brand, model, or size", action: "adjust" },
            { icon: Globe, label: "Allow international", desc: "Expand beyond your market", action: "expand" },
            { icon: Lightbulb, label: "Try common products", desc: "Monitors, headphones, chairs", action: "suggest" },
            { icon: RefreshCw, label: "Retry this search", desc: "Results may improve", action: "retry" },
          ].map((item) => (
            <motion.button
              key={item.action}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => onAction(item.action)}
              className="flex flex-col items-start gap-1 px-4 py-3 rounded-xl border border-border bg-secondary text-left font-body hover:border-primary/30 hover:bg-primary/5 transition-all"
            >
              <div className="flex items-center gap-2">
                <item.icon className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-sm text-secondary-foreground">{item.label}</span>
              </div>
              <span className="text-[10px] text-muted-foreground/70">{item.desc}</span>
            </motion.button>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
